<template>
  <VcWidget>
    <div class="view-cart-items justify-start">
      <VcButton
        type="button"
        variant="link"
        size="lg"
        class="!flex items-center gap-2 text-base font-semibold !text-[--color-neutral-a1]"
        @click="toggleSection"
      >
        <VcIcon
          :name="isViewMoreExpanded ? 'chevron-up' : 'chevron-right'"
          class="mr-2 stroke-2 text-[--color-neutral-a1]"
          :size="20"
        />
        {{ $t("pages.checkout.view_cart_items") }}
      </VcButton>
      <div v-show="isViewMoreExpanded" class="mt-5 flex flex-auto flex-col gap-3">
        <div
          v-for="item in checkoutItems?.slice(0, nbOfVisibleItems)"
          :key="item.lineItem.id"
          class="items flex flex-auto flex-col"
        >
          <div class="flex flex-auto gap-4">
            <div class="flex size-[60px] items-center">
              <VcImage
                :src="getLineItemImageSrc(item.lineItem)"
                alt=""
                :default-size="DEFAULT_DETAIL_THUMB_SIZE"
                :width="DEFAULT_THUMB_SIZE"
                :height="DEFAULT_THUMB_SIZE"
                class="h-[60px] max-w-[60px] object-contain"
              ></VcImage>
            </div>
            <div class="flex w-full flex-col gap-1.5">
              <div class="flex w-full flex-row flex-wrap md:gap-6 xl:gap-10">
                <div class="flex flex-1 flex-col">
                  <div class="flex flex-wrap font-bold leading-6">
                    {{ item.productTitle }}
                  </div>
                  <div class="flex flex-col items-start md:flex-row lg:flex-col xl:flex-row xl:items-center">
                    <span class="flex text-sm font-medium leading-6 text-[--color-neutral-a2]">
                      {{ $t("pages.checkout.quantity") }}: {{ item.lineItem.quantity }}</span
                    >
                    <div
                      v-if="item.lineItem.product?.mainProductId && !item.lineItem.product?.isCalibrationServiceTL"
                      class="cart__serial-number flex flex-col text-sm leading-4 md:flex-row lg:flex-col xl:flex-row"
                    >
                      <span class="mx-2 mt-1 hidden text-[--color-neutral-a3] md:flex lg:hidden xl:flex">|</span>
                      <span class="leading-6 text-[--color-neutral-a2]">
                        {{ $t("shared.cart.serial") }} #: {{ item.lineItem.sku }}
                      </span>
                    </div>
                    <div
                      v-if="item.lineItem.product?.isCalibrationServiceTL"
                      class="cart__serial-number flex flex-col items-start text-sm leading-4 md:flex-row lg:flex-col xl:flex-row xl:items-center"
                    >
                      <span class="mx-2 hidden leading-6 text-[--color-neutral-a3] md:flex lg:hidden xl:flex">|</span>
                      <span class="leading-6 text-[--color-neutral-a2]">
                        {{ $t("shared.cart.item") }} #: {{ item.lineItem.itemNumberTL }}
                      </span>
                      <span class="mx-2 hidden leading-6 text-[--color-neutral-a3] md:flex lg:hidden xl:flex">|</span>
                      <span class="leading-6 text-[--color-neutral-a2]">
                        {{ $t("shared.cart.serial") }} #: {{ item.lineItem.serialNumberTL }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="shouldShowCommentsForOrder(item.lineItem)"
                    class="cart__serial-number mt-2 text-sm leading-4 md:mt-1"
                  >
                    <span>
                      {{ $t("shared.cart.comments") }}:
                      {{
                        item.lineItem.configurationItems?.find((c) => c.customText && c.customText !== "")?.customText
                      }}
                    </span>
                  </div>

                  <span
                    v-if="item.lineItem.product?.isConfigurable"
                    v-dompurify-html="
                      item.lineItem.customDescriptionTL
                        ? item.lineItem.customDescriptionTL
                        : item.lineItem.product?.descriptionsWithFallbacksTL?.find(
                            (description) => description.reviewType === 'Title',
                          )?.content
                    "
                    class="text-sm font-medium leading-6 text-[--color-neutral-a2]"
                  >
                  </span>
                  <span v-if="!isMobile" class="text-sm font-medium italic leading-6 text-[--color-neutral-a2]">{{
                    item.lineItem.customerReferenceTL
                  }}</span>
                </div>
                <div
                  v-if="item.lineItem.placedPrice"
                  class="flex flex-1 flex-col items-end justify-items-end leading-6 md:w-[145px]"
                >
                  <!-- Promo price logic, displayed only if discountTotal exists and is greater than or equal to 0 -->
                  <div
                    v-if="
                      item.lineItem.placedPrice.amount > 0 &&
                      item.lineItem.listPrice?.amount > item.lineItem.placedPrice.amount
                    "
                  >
                    <span class="price-promo flex text-base font-bold leading-6">
                      <VcItemPrice
                        :value="{
                          list: item.lineItem.listPriceAdaptedToQuantityTL!,
                          actual: item.lineItem.extendedPrice,
                        }"
                        :list-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                        :actual-with-tax="item.lineItem.extendedPriceWithTax"
                        :should-price-include-cny-extra-tax="false"
                      />
                    </span>
                  </div>
                  <!-- Original price logic -->
                  <span
                    v-if="!item.lineItem.discounts?.find((d) => d.description === 'VOLUME_CUSTOM')"
                    class="leading-6"
                    :class="{
                      'price flex text-base font-bold leading-6': true,
                      'price-line-trough font-normal text-[--color-neutral-a3] line-through':
                        item.lineItem.placedPrice.amount > 0 &&
                        item.lineItem.listPrice?.amount > item.lineItem.placedPrice.amount, // Apply line-through if there's a discount
                    }"
                  >
                    <VcItemPrice
                      :value="{
                        list: item.lineItem.listPriceAdaptedToQuantityTL!,
                        actual: item.lineItem.listPriceAdaptedToQuantityTL,
                      }"
                      :list-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                      :actual-with-tax="item.lineItem.listPriceWithTaxAdaptedToQuantityTL"
                      :should-price-include-cny-extra-tax="false"
                    />
                  </span>
                  <span class="text-right text-sm font-medium leading-6">
                    {{ $t("shared.catalog.product_details.product_variations.availability") }}: {{ item.status }}
                  </span>
                </div>
              </div>

              <span v-if="isMobile" class="text-sm font-medium italic leading-6 text-[--color-neutral-a2]">{{
                item.lineItem.customerReferenceTL
              }}</span>
            </div>
          </div>
        </div>

        <div v-if="hasShowMoreItemsButton" class="my-1">
          <VcButton
            type="button"
            class="show-items-btn flex items-center"
            variant="link"
            size="lg"
            @click="toggleShowAllItems"
          >
            <VcIcon :name="showAllItems ? 'minus' : 'plus-medium'" :size="12" class="mr-1" />
            {{ showAllItems ? $t("common.buttons.view_less_items") : `${$t("common.buttons.view_all_items")}` }}
          </VcButton>
        </div>

        <!-- Total Section -->
        <div class="mt-1 flex justify-between border-t pt-4 font-semibold leading-6 text-[--color-neutral-a1]">
          <span>{{ totalItemsWithLines }}</span>
          <span
            >{{ $t("common.labels.subtotal") }}:
            <VcPriceDisplay :formatted-value="formatPrice(subTotal, currentCurrency)" />
          </span>
        </div>
      </div>
    </div>
  </VcWidget>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { ref, computed } from "vue";
import { useCurrency } from "@/core/composables";
import { shouldShowCommentsForOrder } from "@/core/utilities/line-items";
import { getLineItemImageSrc } from "@/shared/cart/utils";
import { formatPrice } from "@/shared/cart/utils/formatPrice";
import { useCheckoutFacade } from "../composables/useCheckoutFacade";

const { currentCurrency } = useCurrency();
const { checkoutItems, totalItemsWithLines, subTotal } = useCheckoutFacade();

const isViewMoreExpanded = ref(false);
const showAllItems = ref(false);
const DEFAULT_VISIBLE_ITEMS = 5;

const toggleSection = () => {
  isViewMoreExpanded.value = !isViewMoreExpanded.value;
};

const toggleShowAllItems = () => {
  showAllItems.value = !showAllItems.value;
};

const hasShowMoreItemsButton = computed(() => {
  return checkoutItems.value?.length && checkoutItems.value?.length > DEFAULT_VISIBLE_ITEMS;
});

const nbOfVisibleItems = computed(() => {
  return checkoutItems.value?.length && showAllItems.value ? checkoutItems.value?.length : DEFAULT_VISIBLE_ITEMS;
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const DEFAULT_THUMB_SIZE = 60;
const DEFAULT_DETAIL_THUMB_SIZE = 60;
</script>

<style lang="scss">
.view-cart-items {
  .price-bold {
    @apply font-bold;
  }
}
</style>
